import { useCallback } from 'react';

import { type SizeSettingPreset, type SizeSettingSetMember } from '@air/api';
import { CONFLICT, matchesAirror } from '@air/errors';

import {
  getSizeDimensionsLabel,
  retryCreateOnNameConflict,
  type SavedSize,
  type SaveSetMemberInput,
  type SizeSet,
} from '../utils/savedSizes';
import { usePrivateSizePresetsCreateMutation } from './queries/mutations/usePrivateSizePresetsCreateMutation';
import { usePrivateSizePresetsUpdateMutation } from './queries/mutations/usePrivateSizePresetsUpdateMutation';
import { usePrivateSizeSetsCreateMutation } from './queries/mutations/usePrivateSizeSetsCreateMutation';
import { usePrivateSizeSetsUpdateMutation } from './queries/mutations/usePrivateSizeSetsUpdateMutation';
import { toSizeSet } from './useSavedSizesCatalog';

export type UseSaveSavedSizesParams = {
  /** The full, unfiltered preset list: a new default name is checked against it, so a filtered list would miss a collision. */
  sizePresets: SavedSize[];
  workspaceId: string | undefined;
};

export type SaveCustomSizeParams = {
  /** Retargets this existing saved size's dimensions/name instead of creating a new one. */
  existingSizePresetId?: string;
  height: number;
  /** User-supplied name; omit only for the auto-persist path inside `saveSet`. */
  name?: string;
  width: number;
};

export type { SaveSetMemberInput, UnsavedCustomSizeInput } from '../utils/savedSizes';

export type PersistedCustomSize = {
  /** The session-local `custom_WxH` id this size was saved from, so a caller can re-key its selection. */
  customSizeId: string;
  sizePreset: SizeSettingPreset;
};

export type SaveSetParams = {
  /** Set this to update rather than create, e.g. after a name-collision "Update it" choice. */
  existingSizeSetId?: string;
  /** Ordered selection; the backend persists this sequence as member positions. */
  members: SaveSetMemberInput[];
  name: string;
};

export type SaveSetResult =
  | { persistedCustomSizes: PersistedCustomSize[]; sizeSet: SizeSet; status: 'success' }
  | { error: unknown; failedCustomSizeIds: string[]; persistedCustomSizes: PersistedCustomSize[]; status: 'error' };

export type UseSaveSavedSizesResult = {
  saveCustomSize: (params: SaveCustomSizeParams) => Promise<SizeSettingPreset>;
  saveSet: (params: SaveSetParams) => Promise<SaveSetResult>;
};

/**
 * Lives below `edit-canvas` and `ag-ui-chat` (which edit-canvas already imports) so both can
 * consume it without a cycle.
 */
export const useSaveSavedSizes = ({ sizePresets, workspaceId }: UseSaveSavedSizesParams): UseSaveSavedSizesResult => {
  const createSizePresetMutation = usePrivateSizePresetsCreateMutation();
  const updateSizePresetMutation = usePrivateSizePresetsUpdateMutation();
  const createSizeSetMutation = usePrivateSizeSetsCreateMutation();
  const updateSizeSetMutation = usePrivateSizeSetsUpdateMutation();

  const saveCustomSize = useCallback(
    async ({ existingSizePresetId, height, name, width }: SaveCustomSizeParams): Promise<SizeSettingPreset> => {
      if (!workspaceId) {
        throw new Error('saveCustomSize called before workspaceId was available');
      }

      const trimmedName = name?.trim();

      // An explicit name comes from the bookmark popover or an "Update it" choice -- the user
      // already resolved any collision themselves, so this is a single direct call.
      if (trimmedName) {
        const result = existingSizePresetId
          ? await updateSizePresetMutation.mutateAsync({
              height,
              name: trimmedName,
              sizePresetId: existingSizePresetId,
              width,
              workspaceId,
            })
          : await createSizePresetMutation.mutateAsync({ height, name: trimmedName, width, workspaceId });

        return result.data;
      }

      // No name: the default is picked against a sizePresets list that can be stale against a
      // concurrent create elsewhere, so retry with the next suffix rather than fail over a name
      // nobody chose.
      const sizePreset = await retryCreateOnNameConflict({
        baseName: getSizeDimensionsLabel({ height, width }),
        create: (candidateName) =>
          createSizePresetMutation.mutateAsync({ height, name: candidateName, width, workspaceId }),
        existingNames: sizePresets.map((sizePreset: SavedSize) => sizePreset.name),
        isConflictError: (error) => matchesAirror(error, CONFLICT),
      });

      return sizePreset.data;
    },
    [createSizePresetMutation, sizePresets, updateSizePresetMutation, workspaceId],
  );

  const saveSet = useCallback(
    async ({ existingSizeSetId, members: requestedMembers, name }: SaveSetParams): Promise<SaveSetResult> => {
      if (!workspaceId) {
        throw new Error('saveSet called before workspaceId was available');
      }

      const persistedCustomSizes: PersistedCustomSize[] = [];
      const failedCustomSizeIds: string[] = [];
      let persistError: unknown;
      const unsavedCustomSizes = requestedMembers.flatMap((member) =>
        member.type === 'customSize' ? [member.customSize] : [],
      );

      const persistResults = await Promise.allSettled(
        unsavedCustomSizes.map((customSize) =>
          saveCustomSize({ height: customSize.height, width: customSize.width }).then(
            (sizePreset): PersistedCustomSize => ({ customSizeId: customSize.id, sizePreset }),
          ),
        ),
      );

      // Zipped by index rather than read off `result.value`/`result.reason` alone: a rejected
      // result carries no id, and only unsavedCustomSizes has one for every position.
      const persistOutcomes = unsavedCustomSizes.map((customSize, index) => ({
        customSize,
        result: persistResults[index],
      }));

      for (const { customSize, result } of persistOutcomes) {
        if (result.status === 'fulfilled') {
          persistedCustomSizes.push(result.value);
        } else {
          failedCustomSizeIds.push(customSize.id);
          persistError = result.reason;
        }
      }

      // A partial persistence failure never reaches the set write -- reporting success here would
      // silently drop the failed members from the set. Members that did persist are still
      // returned so the caller can fold them into its selection and avoid re-creating them on retry.
      if (failedCustomSizeIds.length > 0) {
        return { error: persistError, failedCustomSizeIds, persistedCustomSizes, status: 'error' };
      }

      const persistedCustomSizesById = new Map(
        persistedCustomSizes.map((persistedCustomSize) => [persistedCustomSize.customSizeId, persistedCustomSize]),
      );
      const members: SizeSettingSetMember[] = requestedMembers.flatMap((member): SizeSettingSetMember[] => {
        if (member.type !== 'customSize') {
          return [member];
        }

        const persistedCustomSize = persistedCustomSizesById.get(member.customSize.id);

        return persistedCustomSize ? [{ sizePresetId: persistedCustomSize.sizePreset.id, type: 'sizePreset' }] : [];
      });

      try {
        const result = existingSizeSetId
          ? await updateSizeSetMutation.mutateAsync({ members, name, sizeSetId: existingSizeSetId, workspaceId })
          : await createSizeSetMutation.mutateAsync({ members, name, workspaceId });

        return { persistedCustomSizes, sizeSet: toSizeSet(result.data), status: 'success' };
      } catch (error) {
        // The custom sizes above are already real, persisted saved sizes even though the set
        // write failed — the caller must not re-create them on retry, only re-attempt the set.
        return { error, failedCustomSizeIds: [], persistedCustomSizes, status: 'error' };
      }
    },
    [createSizeSetMutation, saveCustomSize, updateSizeSetMutation, workspaceId],
  );

  return {
    saveCustomSize,
    saveSet,
  };
};
